'use client';

import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapIcon,
  ClockIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import {
  OperationalHeader,
  OperationalPage,
  OperationalSection,
  OperationalSource,
  OperationalState,
  OperationalStatus,
  Surface,
} from '@/app/Components/OperationalPage';
import { Field, Input, Select } from '@/app/Components/Field';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import RoomLocationLink from '@/app/Components/RoomLocationLink';
import AssistantButton from '@/app/Components/Assistant/AssistantButton';
import {
  buildRoomAvailabilitySemesterUrl,
  buildRoomAvailabilityViewUrl,
  clampRoomSearchStartMinute,
  getRoomAvailabilitySemester,
  getDefaultRoomSearchStartMinute,
  resolveRoomAvailabilitySemester,
  resolveRoomAvailabilityViewMode,
  ROOM_AVAILABILITY_SEMESTERS,
  ROOM_DAY_END_MINUTE,
  ROOM_DAY_START_MINUTE,
  type RoomAvailabilitySemesterId,
  type RoomAvailabilityViewMode,
} from './viewModel';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREV: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

// Operating hours: 7:00–22:00 (minutes from midnight)
const DAY_START_MIN = ROOM_DAY_START_MINUTE;
const DAY_END_MIN = ROOM_DAY_END_MINUTE;

interface RoomInfo {
  roomKey: string;
  building_code: string;
  building_num: string;
  room_number: string;
  campus_abbrev: string;
  campus_name: string;
  label: string;
}

interface UsageBlock {
  day: string;
  startMinute: number;
  endMinute: number;
}

interface RoomAvailabilityData {
  rooms: RoomInfo[];
  usage: Record<string, UsageBlock[]>;
  dayOrder: string[];
}

function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

function durationMinutes(start: number, end: number): number {
  return end - start;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Compute available intervals for one day: [DAY_START, DAY_END] minus busy blocks. */
function availableIntervals(
  busy: { startMinute: number; endMinute: number }[]
): { start: number; end: number }[] {
  const sorted = [...busy].sort((a, b) => a.startMinute - b.startMinute);
  const out: { start: number; end: number }[] = [];
  let cur = DAY_START_MIN;
  for (const b of sorted) {
    const start = Math.max(b.startMinute, DAY_START_MIN);
    const end = Math.min(b.endMinute, DAY_END_MIN);
    if (start > cur && end > cur) {
      out.push({ start: cur, end: Math.min(start, DAY_END_MIN) });
    }
    cur = Math.max(cur, end);
  }
  if (cur < DAY_END_MIN) {
    out.push({ start: cur, end: DAY_END_MIN });
  }
  return out;
}

/** True if room has no usage overlapping [startMinute, endMinute) on the given day. */
function isRoomFreeForBlock(
  usage: Record<string, UsageBlock[]>,
  roomKey: string,
  day: string,
  startMinute: number,
  endMinute: number
): boolean {
  if (startMinute < DAY_START_MIN || endMinute > DAY_END_MIN) return false;
  const blocks = (usage[roomKey] ?? []).filter((b) => b.day === day);
  for (const b of blocks) {
    if (b.startMinute < endMinute && b.endMinute > startMinute) return false;
  }
  return true;
}

// Unique building key: building_code|campus_abbrev
function buildingKey(r: RoomInfo): string {
  return `${r.building_code}|${r.campus_abbrev}`;
}

function buildingLabel(r: RoomInfo): string {
  return `${r.building_code} (${r.campus_abbrev})`;
}

const DURATION_OPTIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

function RoomAvailabilityPage() {
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();
  const router = useRouter();
  const [data, setData] = useState<RoomAvailabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [viewMode, setViewMode] = useState<RoomAvailabilityViewMode>(() =>
    resolveRoomAvailabilityViewMode(new URLSearchParams(searchParamsString))
  );
  const [selectedSemester, setSelectedSemester] = useState<RoomAvailabilitySemesterId>(() =>
    resolveRoomAvailabilitySemester(new URLSearchParams(searchParamsString))
  );
  const semester = getRoomAvailabilitySemester(selectedSemester);

  const setViewModeWithUrl = (mode: RoomAvailabilityViewMode) => {
    setViewMode(mode);
    router.replace(buildRoomAvailabilityViewUrl(new URLSearchParams(searchParamsString), mode), { scroll: false });
  };
  const setSemesterWithUrl = (nextSemester: RoomAvailabilitySemesterId) => {
    setData(null);
    setLoading(true);
    setSelectedSemester(nextSemester);
    setSelectedBuildingKey(null);
    setSelectedRoomKey(null);
    setBuildingSearch('');
    setRoomSearch('');
    setUrlBuildingApplied(false);
    router.replace(
      buildRoomAvailabilitySemesterUrl(
        new URLSearchParams(searchParamsString),
        nextSemester,
      ),
      { scroll: false },
    );
  };
  const [selectedBuildingKey, setSelectedBuildingKey] = useState<string | null>(null);
  const [selectedRoomKey, setSelectedRoomKey] = useState<string | null>(null);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [urlBuildingApplied, setUrlBuildingApplied] = useState(false);

  // Find-rooms view state
  const [findDay, setFindDay] = useState(() => {
    const d = new Date().getDay();
    return DAY_ORDER[d === 0 ? 6 : d - 1];
  });
  const [findStartMinute, setFindStartMinute] = useState(() => getDefaultRoomSearchStartMinute(new Date()));
  const [findDuration, setFindDuration] = useState(60);
  const [findCampus, setFindCampus] = useState<string>('');

  useEffect(() => {
    setSelectedSemester(resolveRoomAvailabilitySemester(new URLSearchParams(searchParamsString)));
  }, [searchParamsString]);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setLoading(true);
    setError(null);
    fetch(semester.dataPath, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load room data');
        return res.json();
      })
      .then((d: RoomAvailabilityData) => {
        if (!controller.signal.aborted) setData(d);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadAttempt, semester.dataPath]);

  // Pre-select building from URL ?building=CODE|CAMPUS (e.g. from dashboard quick search)
  useEffect(() => {
    if (urlBuildingApplied || !data) return;
    const buildingParam = searchParams.get('building');
    if (!buildingParam) return;
    const key = buildingParam.trim();
    const exists = data.rooms.some((r) => buildingKey(r) === key);
    if (exists) {
      setSelectedBuildingKey(key);
      const room = data.rooms.find((r) => buildingKey(r) === key);
      if (room) setBuildingSearch(buildingLabel(room));
      setUrlBuildingApplied(true);
    }
  }, [data, searchParams, urlBuildingApplied]);

  // Unique buildings (building_code + campus), sorted by campus then code
  const buildings = useMemo(() => {
    if (!data) return [];
    const seen = new Set<string>();
    const list: { key: string; label: string }[] = [];
    for (const r of data.rooms) {
      const key = buildingKey(r);
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ key, label: buildingLabel(r) });
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const filteredBuildings = useMemo(() => {
    const q = buildingSearch.trim().toLowerCase();
    if (!q) return buildings;
    return buildings.filter((b) => b.label.toLowerCase().includes(q));
  }, [buildings, buildingSearch]);

  const roomsInBuilding = useMemo(() => {
    if (!data || !selectedBuildingKey) return [];
    const [code, campus] = selectedBuildingKey.split('|');
    return data.rooms.filter(
      (r) => r.building_code === code && r.campus_abbrev === campus
    );
  }, [data, selectedBuildingKey]);

  const filteredRooms = useMemo(() => {
    const q = roomSearch.trim().toLowerCase();
    if (!q) return roomsInBuilding;
    return roomsInBuilding.filter((r) =>
      r.room_number.toLowerCase().includes(q)
    );
  }, [roomsInBuilding, roomSearch]);

  // When building changes, pick first room in that building (or clear if none)
  useEffect(() => {
    if (!selectedBuildingKey) {
      setSelectedRoomKey(null);
      return;
    }
    const first = roomsInBuilding[0];
    setSelectedRoomKey(first?.roomKey ?? null);
  }, [selectedBuildingKey, roomsInBuilding]);

  const selectedRoom = useMemo(() => {
    if (!data || !selectedRoomKey) return null;
    return data.rooms.find((r) => r.roomKey === selectedRoomKey) ?? null;
  }, [data, selectedRoomKey]);
  const assistantContext = useMemo(() => ({
    selectedEntity: selectedRoom
      ? { kind: 'room', id: selectedRoom.roomKey, label: selectedRoom.label }
      : undefined,
    availablePageActions: ['find_available_rooms', 'inspect_building', 'review_room_schedule'],
  }), [selectedRoom]);

  const usageByDay = useMemo(() => {
    if (!data || !selectedRoomKey) return {};
    const blocks = data.usage[selectedRoomKey] ?? [];
    const byDay: Record<string, { startMinute: number; endMinute: number }[]> = {};
    for (const b of blocks) {
      if (!byDay[b.day]) byDay[b.day] = [];
      byDay[b.day].push({ startMinute: b.startMinute, endMinute: b.endMinute });
    }
    return byDay;
  }, [data, selectedRoomKey]);

  const availabilityByDay = useMemo(() => {
    const result: Record<string, { start: number; end: number }[]> = {};
    for (const day of DAY_ORDER) {
      result[day] = availableIntervals(usageByDay[day] ?? []);
    }
    return result;
  }, [usageByDay]);

  // Unique campuses for find view (sorted)
  const campuses = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.rooms.map((r) => r.campus_abbrev).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  // Rooms available for the full [findStartMinute, findStartMinute + findDuration] on findDay
  const findAvailableRooms = useMemo(() => {
    if (!data) return [];
    const endMinute = findStartMinute + findDuration;
    return data.rooms.filter((r) => {
      if (findCampus && r.campus_abbrev !== findCampus) return false;
      return isRoomFreeForBlock(
        data.usage,
        r.roomKey,
        findDay,
        findStartMinute,
        endMinute
      );
    });
  }, [data, findDay, findStartMinute, findDuration, findCampus]);

  if (loading) {
    return <PageLoading title="Loading room schedules" description={`Preparing the checked-in ${semester.label} room availability snapshot.`} />;
  }

  if (error || !data) {
    return (
      <OperationalPage className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <OperationalHeader
          eyebrow="Campus spaces"
          title="Room availability"
          description="Find a classroom without a scheduled class block during the time you need."
        />
        <Surface>
          <OperationalState
            kind="hard-failure"
            title="Room availability data is unavailable"
            description={`${error ?? `The ${semester.label} room dataset could not be loaded right now.`} This is a checked-in schedule snapshot, not a live room-booking system.`}
            action={<button type="button" onClick={() => setLoadAttempt((value) => value + 1)} className="btn-primary">Retry room snapshot</button>}
          />
        </Surface>
      </OperationalPage>
    );
  }

  return (
    <>
      <AssistantButton context={assistantContext} />
      <OperationalPage className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <OperationalHeader
        eyebrow="Campus spaces"
        title="Find an available room"
        description={`Check where the ${semester.label} class schedule leaves a room free, then verify building access and reservations in person.`}
        actions={
          <Link
            href={buildRoomAvailabilitySemesterUrl(new URLSearchParams(), selectedSemester, '/room-availability/map')}
            className="btn-secondary"
          >
            <MapIcon className="h-5 w-5" aria-hidden="true" />
            Show on map
          </Link>
        }
      />

      <Surface variant="inset" className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0">
          <OperationalSource kind="checked-in" source={`${semester.label} room-schedule snapshot`} />
          <p className="mt-2 text-sm leading-6 text-[var(--ss-text-muted)]">
            “Available” means no class block appears between 7:00 and 22:00. It does not confirm live occupancy, unlocked doors, or reservations.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-[10rem_auto] sm:items-end">
          <Field label="Semester">
            {(fieldProps) => (
              <Select
                {...fieldProps}
                size="lg"
                value={selectedSemester}
                onChange={(event) => setSemesterWithUrl(event.target.value as RoomAvailabilitySemesterId)}
              >
                {ROOM_AVAILABILITY_SEMESTERS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </Select>
            )}
          </Field>
          <div className="grid grid-cols-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] p-1" role="group" aria-label="Room search mode">
            <button
              type="button"
              aria-pressed={viewMode === 'browse'}
              onClick={() => setViewModeWithUrl('browse')}
              className={`ss-button px-3 ${viewMode === 'browse' ? 'bg-[var(--ss-brand)] text-[var(--ss-brand-contrast)]' : 'text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]'}`}
            >
              <ListBulletIcon className="h-4 w-4" aria-hidden="true" /> Browse
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'find'}
              onClick={() => setViewModeWithUrl('find')}
              className={`ss-button px-3 ${viewMode === 'find' ? 'bg-[var(--ss-brand)] text-[var(--ss-brand-contrast)]' : 'text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]'}`}
            >
              <ClockIcon className="h-4 w-4" aria-hidden="true" /> Find by time
            </button>
          </div>
        </div>
      </Surface>

      {viewMode === 'find' ? (
        <OperationalSection title="When do you need a room?" description="Choose a complete time block. Results stay within the snapshot’s 7:00–22:00 operating window.">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Day">
              {(fieldProps) => (
                  <Select
                    {...fieldProps}
                    size="lg"
                    value={findDay}
                    onChange={(e) => setFindDay(e.target.value)}
                    className="ss-field"
                  >
                    {DAY_ORDER.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Select>
              )}
            </Field>
            <Field label="Start time">
              {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    size="lg"
                    type="time"
                    value={`${String(Math.floor(findStartMinute / 60)).padStart(2, '0')}:${String(findStartMinute % 60).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const [h, m] = e.target.value.split(':').map(Number);
                      setFindStartMinute(clampRoomSearchStartMinute(h * 60 + m, findDuration));
                    }}
                    min="07:00"
                    max={formatMinutes(DAY_END_MIN - findDuration)}
                    className="ss-field"
                  />
              )}
            </Field>
            <Field label="Duration">
              {(fieldProps) => (
                  <Select
                    {...fieldProps}
                    size="lg"
                    value={findDuration}
                    onChange={(e) => {
                      const duration = Number(e.target.value);
                      setFindDuration(duration);
                      setFindStartMinute((minute) => clampRoomSearchStartMinute(minute, duration));
                    }}
                    className="ss-field"
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
              )}
            </Field>
            <Field label="Campus">
              {(fieldProps) => (
                  <Select
                    {...fieldProps}
                    size="lg"
                    value={findCampus}
                    onChange={(e) => setFindCampus(e.target.value)}
                    className="ss-field"
                  >
                    <option value="">Any campus</option>
                    {campuses.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
              )}
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2" role="status" aria-live="polite">
            <OperationalStatus tone={findAvailableRooms.length ? 'positive' : 'neutral'}>{findAvailableRooms.length} rooms</OperationalStatus>
            <p className="text-sm text-[var(--ss-text-muted)]">
              Free from {formatMinutes(findStartMinute)}–{formatMinutes(findStartMinute + findDuration)} on {findDay}{findCampus ? ` at ${findCampus}` : ''}
            </p>
          </div>
          {findAvailableRooms.length === 0 ? (
            <div className="mt-4"><OperationalState kind="empty" title="No rooms are free for that full period" description="Try a different time, shorten the duration, or widen the campus filter." /></div>
          ) : (
            <ul className="mt-4 grid max-h-[60vh] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3" aria-label="Available rooms">
              {findAvailableRooms.map((r) => (
                <li key={r.roomKey} className="flex min-h-12 items-center gap-3 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-4 py-3">
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-[var(--ss-positive)]" aria-hidden="true" />
                  <RoomLocationLink location={`${r.building_code} ${r.room_number}`} className="font-medium text-[var(--ss-text)]" />
                </li>
              ))}
            </ul>
          )}
        </OperationalSection>
      ) : (
        <>
          <OperationalSection title="Choose a room" description="Search a building, select a room, then review its weekly free and in-use blocks.">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="grid gap-2">
                <label htmlFor="building-search" className="text-sm font-semibold text-[var(--ss-text)]">1. Search by building</label>
                <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--ss-text-muted)]" aria-hidden="true" />
                    <input
                      id="building-search"
                      type="text"
                      placeholder="Search buildings (e.g. ARC, ABE, Busch)…"
                      value={buildingSearch}
                      onChange={(e) => {
                        const value = e.target.value;
                        setBuildingSearch(value);
                        const selectedBuilding = buildings.find((building) => building.key === selectedBuildingKey);
                        if (selectedBuilding && !selectedBuilding.label.toLowerCase().includes(value.trim().toLowerCase())) {
                          setSelectedBuildingKey(null);
                          setSelectedRoomKey(null);
                          setRoomSearch('');
                        }
                      }}
                      className="ss-field pl-10"
                    />
                  </div>
                  <select
                    aria-label="Building"
                    value={selectedBuildingKey ?? ''}
                    onChange={(e) => {
                      setRoomSearch('');
                      setSelectedBuildingKey(e.target.value || null);
                    }}
                    className="ss-field"
                  >
                    <option value="">Select a building…</option>
                    {filteredBuildings.map((b) => (
                      <option key={b.key} value={b.key}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                  {buildingSearch && filteredBuildings.length === 0 ? <p className="text-sm text-[var(--ss-warning)]" role="status">No buildings match this search.</p> : null}
              </div>

              <div className="grid gap-2">
                <label htmlFor="room-search" className="text-sm font-semibold text-[var(--ss-text)]">2. Search by room</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--ss-text-muted)]" aria-hidden="true" />
                    <input
                      id="room-search"
                      type="text"
                      placeholder={selectedBuildingKey ? 'Search rooms (e.g. 203, 105)…' : 'Select a building first'}
                      value={roomSearch}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRoomSearch(value);
                        const selectedRoom = roomsInBuilding.find((room) => room.roomKey === selectedRoomKey);
                        if (selectedRoom && !selectedRoom.room_number.toLowerCase().includes(value.trim().toLowerCase())) {
                          setSelectedRoomKey(null);
                        }
                      }}
                      disabled={!selectedBuildingKey}
                      className="ss-field pl-10 disabled:cursor-not-allowed"
                    />
                  </div>
                  <select
                    aria-label="Room"
                    value={selectedRoomKey ?? ''}
                    onChange={(e) => setSelectedRoomKey(e.target.value || null)}
                    disabled={!selectedBuildingKey}
                    className="ss-field disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!selectedBuildingKey ? 'Select a building first' : filteredRooms.length === 0 ? 'No rooms match' : 'Select a room…'}
                    </option>
                    {filteredRooms.map((r) => (
                      <option key={r.roomKey} value={r.roomKey}>
                        {r.room_number} — {r.label}
                      </option>
                    ))}
                  </select>
              </div>
            </div>
          </OperationalSection>

          {selectedRoom ? (
            <div data-audit-ready="room-availability-populated" data-audit-state="populated">
              <OperationalSection
              title={selectedRoom.label}
              description="Weekly schedule-derived availability"
              actions={<div className="flex flex-wrap items-center gap-2"><OperationalStatus tone="info"><CalendarDaysIcon className="h-4 w-4" aria-hidden="true" /> {semester.label}</OperationalStatus><RoomLocationLink location={`${selectedRoom.building_code} ${selectedRoom.room_number}`} className="text-sm font-semibold" /></div>}
            >
                <div className="divide-y divide-[var(--ss-border)]">
                  {DAY_ORDER.map((day) => {
                    const busy = usageByDay[day] ?? [];
                    const available = availabilityByDay[day] ?? [];
                    return (
                      <section key={day} className="grid gap-4 py-4 first:pt-0 last:pb-0 md:grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)]">
                        <h3 className="font-semibold text-[var(--ss-text)]">{DAY_ABBREV[day]}<span className="sr-only"> — {day}</span></h3>
                        <div>
                          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[var(--ss-text-muted)]">
                            <XCircleIcon className="h-4 w-4 text-[var(--ss-critical)]" aria-hidden="true" />
                            In use
                          </h4>
                          {busy.length === 0 ? (
                            <p className="text-sm text-[var(--ss-text-muted)]">
                              No scheduled use this day.
                            </p>
                          ) : (
                            <ul className="space-y-1.5">
                              {busy.map((b, i) => (
                                <li
                                  key={i}
                                  className="flex items-center gap-2 text-sm text-[var(--ss-text)]"
                                >
                                  <span className="font-mono">
                                    {formatMinutes(b.startMinute)}–{formatMinutes(b.endMinute)}
                                  </span>
                                  <span className="text-[var(--ss-text-muted)]">
                                    ({formatDuration(durationMinutes(b.startMinute, b.endMinute))})
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[var(--ss-text-muted)]">
                              <CheckCircleIcon className="h-4 w-4 text-[var(--ss-positive)]" aria-hidden="true" />
                              Available
                            </h4>
                            {available.length === 0 ? (
                              <p className="text-sm text-[var(--ss-text-muted)]">
                                No availability this day (or outside 7:00–22:00).
                              </p>
                            ) : (
                              <ul className="space-y-1.5">
                                {available.map((a, i) => (
                                  <li
                                    key={i}
                                    className="flex items-center gap-2 text-sm text-[var(--ss-text)]"
                                  >
                                    <span className="font-mono">
                                      {formatMinutes(a.start)}–{formatMinutes(a.end)}
                                    </span>
                                    <span className="font-medium text-[var(--ss-positive)]">
                                      {formatDuration(durationMinutes(a.start, a.end))}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </OperationalSection>
            </div>
          ) : selectedBuildingKey && filteredRooms.length === 0 ? (
            <OperationalState kind="empty" title="No rooms match your search" description="Clear the room query or pick another building to keep browsing availability." />
          ) : (
            <OperationalState kind="empty" title="Choose a building to begin" description="Select a building and room to review the week’s schedule-derived availability." />
          )}
        </>
      )}
      </OperationalPage>
    </>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><RoomAvailabilityPage /></RouteSuspense>; }
